/**
 * Category Stats Card — donut chart showing habit distribution by category.
 *
 * Desktop: donut left, "Kategoriyalar" title + legend right-center, grows horizontal.
 * Mobile: donut centered top, legend centered below in 2-3 cols.
 * Centered on page via mx-auto.
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import { useMonthlyHabits } from '@turnifly/shared/hooks'
import type { MonthlyHabitsResponse } from '@turnifly/shared/types'

/* ── Category colors (only known categories) ── */

const CAT_COLORS: Record<string, string> = {
  sport: '#22C55E', education: '#3B82F6', spiritual: '#A78BFA', business: '#F59E0B',
  health: '#EF4444', creativity: '#EC4899', mental: '#06B6D4', tech: '#6366F1',
  language: '#14B8A6', lifestyle: '#84CC16',
  family: '#F97316', work: '#8B5CF6',
  personal_growth: '#F43F5E', finance: '#10B981', social: '#FBBF24',
  sleep: '#818CF8', rest: '#38BDF8', tidiness: '#A3E635',
}

const CAT_UZ: Record<string, string> = {
  sport: 'Sport', education: "Ta'lim", spiritual: 'Ruhiy', business: 'Biznes',
  health: "Sog'liq", creativity: 'Ijod', mental: 'Aqliy', tech: 'IT',
  language: "Til o'rganish", lifestyle: 'Turmush tarzi',
  family: 'Oila', work: 'Ish',
  personal_growth: 'Shaxsiy o\'sish', finance: 'Moliya', social: 'Ijtimoiy',
  sleep: 'Uyqu', rest: 'Dam olish', tidiness: 'Tartib',
}
const CAT_EN: Record<string, string> = {
  sport: 'Sport', education: 'Education', spiritual: 'Spiritual', business: 'Business',
  health: 'Health', creativity: 'Creativity', mental: 'Mental', tech: 'Technology',
  language: 'Language', lifestyle: 'Lifestyle',
  family: 'Family', work: 'Work',
  personal_growth: 'Personal Growth', finance: 'Finance', social: 'Social',
  sleep: 'Sleep', rest: 'Rest', tidiness: 'Tidiness',
}

function catName(id: string, lang: string): string {
  return (lang === 'uz' ? CAT_UZ[id] : CAT_EN[id]) ?? id
}

interface Slice { id: string; count: number; percent: number; color: string }

/* ── Donut ── */
const SZ = 110
const SW = 12
const R = (SZ - SW) / 2
const C = 2 * Math.PI * R

/* ═══ MAIN ═══ */

export const CategoryStatsCard: React.FC = React.memo(() => {
  const { t, i18n } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id)
  const { year, month } = useHomeStore()
  const { data } = useMonthlyHabits(userId, year, month)
  const lang = i18n.language

  const slices = useMemo((): Slice[] => {
    if (!data) return []
    const habits = (data as MonthlyHabitsResponse).habits ?? []
    if (habits.length === 0) return []

    const counts = new Map<string, number>()
    for (const h of habits) {
      const cat = h.category
      // Fallback for unknown/empty categories
      const key = cat && CAT_COLORS[cat] ? cat : '_other'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }

    const total = habits.length
    const result: Slice[] = []
    for (const [id, count] of counts) {
      if (id === '_other') continue // skip "other" from donut; they still count in total
      result.push({ id, count, percent: Math.round((count / total) * 100), color: CAT_COLORS[id] })
    }
    result.sort((a, b) => b.count - a.count)
    return result
  }, [data])

  if (slices.length === 0) return null

  let offset = 0
  const segments = slices.map((s) => {
    const len = (s.percent / 100) * C
    const seg = { ...s, da: `${len} ${C - len}`, off: -offset }
    offset += len
    return seg
  })

  const total = (data as MonthlyHabitsResponse | undefined)?.habits?.length ?? slices.reduce((s, c) => s + c.count, 0)

  // Legend grid cols: auto based on count
  const legendCols = slices.length <= 3 ? 'grid-cols-1' : slices.length <= 6 ? 'grid-cols-2' : 'grid-cols-3'

  return (
    <div className="rounded-2xl glass overflow-hidden p-4 lg:py-6 w-full">
      {/* Mobile: column (donut top, legend bottom). Desktop: row centered with spacing */}
      <div className="flex flex-col lg:flex-row items-center justify-center gap-5 lg:gap-10">
        {/* Donut */}
        <div className="shrink-0 relative" style={{ width: SZ, height: SZ }}>
          <svg width={SZ} height={SZ} viewBox={`0 0 ${SZ} ${SZ}`} className="-rotate-90">
            <circle cx={SZ / 2} cy={SZ / 2} r={R} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={SW} />
            {segments.map((s) => (
              <circle key={s.id} cx={SZ / 2} cy={SZ / 2} r={R} fill="none" stroke={s.color} strokeWidth={SW} strokeDasharray={s.da} strokeDashoffset={s.off} strokeLinecap="round" className="transition-all duration-700 ease-out" />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[18px] font-display font-bold text-text tabular-nums">{total}</span>
            <span className="text-[9px] font-medium" style={{ color: '#768c9e' }}>{t('home.totalHabits')}</span>
          </div>
        </div>

        {/* Legend */}
        <div className="min-w-0 flex flex-col items-center lg:items-start">
          <h3 className="text-[13px] font-display font-bold text-text mb-2.5">{t('home.categoryStats')}</h3>
          <div className={`grid gap-x-5 gap-y-1.5 ${legendCols}`}>
            {slices.map((s) => (
              <div key={s.id} className="flex items-center gap-1.5 min-w-0">
                <div className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-[11px] text-text truncate">{catName(s.id, lang)}</span>
                <span className="text-[10px] font-bold tabular-nums shrink-0 ml-1" style={{ color: '#768c9e' }}>{s.percent}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})
CategoryStatsCard.displayName = 'CategoryStatsCard'
